import { useCallback, useEffect, useRef, useState } from 'react';

import { BASE_OVERRIDE, CHAIN_BY_ID, SUPPORTED_CHAINS } from '../config';
import { SUPPORTED_CHAIN_IDS } from '../config/config.types';
import { isMocked } from '../utils/isMocked';
import * as Sentry from '@sentry/react';
import { DEFAULT_MOCK_ADDRESS } from 'e2e-tests/mocks/mock-rooms';
import {
  isStreamPath,
  MOCK_ACCOUNT,
  MOCK_BLOCK_NUMBER,
  MOCK_PUBLIC_CLIENT,
  MOCK_READ_CONTRACT,
  MOCK_SIGN_MESSAGE,
  MOCK_WALLET_CLIENT,
} from 'e2e-tests/mocks/wagmi.mocks';
import { type Address, Chain, type Hash, type PublicClient, WalletClient } from 'viem';
import { watchBlockNumber } from 'viem/actions';

import { UIConnector, useWeb3ClientsStore } from 'shared/store/web3-clients-store';
import { WriteError } from 'shared/types/app-types';

type UseBlockNumberResult = {
  data: bigint | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

type AccountEffectOpts = {
  onConnect?: (data: { address: Address; chainId: number | null }) => void;
  onDisconnect?: () => void;
};

type QueryOptions = {
  enabled?: boolean;
  refetchInterval?: number | false;
  retry?: boolean;
  retryDelay?: number;
  staleTime?: number;
  gcTime?: number;
};

type UseWaitForTxArgs = {
  hash?: Hash;
  confirmations?: number;
  query?: QueryOptions;
};

const useAccount = () => {
  if (isMocked) {
    return MOCK_ACCOUNT;
  }
  const { address, chainId, isAuthenticated, isPrivyWallet, logout } = useWeb3ClientsStore((s) => ({
    address: s.address,
    chainId: s.chainId,
    isAuthenticated: s.isAuthenticated,
    isPrivyWallet: s.isPrivyWallet,
    logout: s.logout,
  }));

  const chain: Chain =
    chainId && CHAIN_BY_ID[chainId] ? (CHAIN_BY_ID[chainId] as Chain) : BASE_OVERRIDE;

  return {
    address,
    chainId,
    isConnected: Boolean(isAuthenticated && address),
    isConnecting: false,
    chain,
    disconnect: logout ?? (async () => {}),
    isPrivyWallet,
  };
};

const usePublicClient = () =>
  isMocked && !isStreamPath() ? MOCK_PUBLIC_CLIENT : useWeb3ClientsStore((s) => s.publicClient);

const useWalletClient = () => {
  const client = useWeb3ClientsStore((s) => s.walletClient);
  if (isMocked && !isStreamPath()) {
    return MOCK_WALLET_CLIENT as unknown as WalletClient;
  }
  return client;
};

const useChainId = () =>
  isMocked ? SUPPORTED_CHAIN_IDS.ARBITRUM : useWeb3ClientsStore((s) => s.chainId);

const useFundWalletHandler = () => {
  const { address, fundPrivyWallet } = useWeb3ClientsStore((s) => ({
    address: s.address,
    fundPrivyWallet: s.fundPrivyWallet,
  }));

  const handleBuy = async (_amount = '1') => {
    if (!address || !fundPrivyWallet) return;
    try {
      await fundPrivyWallet({ address: address as Address });
    } catch (err) {
      console.error('Funding failed:', err);
    }
  };

  return handleBuy;
};

function useBlockNumber({ watch = true }: { watch?: boolean } = {}): UseBlockNumberResult {
  if (isMocked) {
    return MOCK_BLOCK_NUMBER;
  }
  const publicClient = usePublicClient();
  const [data, setData] = useState<bigint>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!publicClient) return;
    try {
      setIsLoading(true);
      const blockNumber = await publicClient.getBlockNumber();
      setData(blockNumber);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [publicClient]);

  useEffect(() => {
    if (!publicClient) return;
    let unwatch: (() => void) | undefined;
    void refetch();

    if (watch) {
      try {
        unwatch = watchBlockNumber(publicClient as any, {
          onBlockNumber: (bn) => setData(bn),
          onError: (err) => setError(err),
        });
      } catch (err) {
        console.error('watchBlockNumber failed:', err);
      }
    }
    return () => {
      unwatch?.();
    };
  }, [publicClient, refetch, watch]);

  return { data, isLoading, error, refetch };
}

const useSwitchChain = () => {
  const { ethereumProvider } = useWeb3ClientsStore();
  const [error, setError] = useState<Error | null>(null);

  const switchChain = async (targetId: number) => {
    // prefer the provider stored by your bootstrap; fall back to window.ethereum for wagmi/injected
    const provider = ethereumProvider ?? (globalThis as any)?.ethereum;
    if (!provider) return;

    const hexId = `0x${targetId.toString(16)}`;
    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexId }],
      });
    } catch (err: any) {
      if (err?.code === 4902 || /unrecognized chain/i.test(err?.message || '')) {
        const chain = CHAIN_BY_ID[targetId] as Chain | undefined;
        if (!chain) throw new Error(`Unsupported chainId ${targetId}`);
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: hexId,
              chainName: chain.name,
              nativeCurrency: chain.nativeCurrency,
              rpcUrls: chain.rpcUrls.default.http,
              blockExplorerUrls: chain.blockExplorers
                ? [chain.blockExplorers.default.url]
                : undefined,
            },
          ],
        });
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: hexId }],
        });
      } else {
        const e = err instanceof Error ? err : new Error('Failed to switch chain');
        setError(e);
        throw e;
      }
    }
  };

  return {
    switchChain,
    switchChainAsync: async ({ chainId }: { chainId: number }) => switchChain(chainId),
    chains: SUPPORTED_CHAINS as Chain[],
    error,
  };
};

const useSignMessage = () => {
  if (isMocked) {
    return MOCK_SIGN_MESSAGE;
  }

  const { walletClient, address } = useWeb3ClientsStore();
  const [data, setData] = useState<`0x${string}` | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const signMessage = async ({ account, message }: { account?: Address; message: string }) => {
    const acct = (account ?? address) as Address | null;
    if (!walletClient || !acct) {
      const err = new Error('Wallet client or address not available');
      setError(err);
      throw err;
    }
    try {
      setIsLoading(true);
      setError(null);
      const sig = await walletClient.signMessage({ account: acct, message });
      setData(sig);
      return sig;
    } catch (e) {
      const typed = e as WriteError | undefined;
      if (typed?.cause?.code && typed.cause.code !== 4001) {
        Sentry.captureException(e);
        setError(e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { data, isLoading, error, signMessage, signMessageAsync: signMessage };
};

const useDisconnect = () => {
  const logout = useWeb3ClientsStore((s) => s.logout);
  return {
    disconnect: async () => {
      if (logout) await logout();
    },
    disconnectAsync: async () => {
      if (logout) await logout();
    },
  };
};

const useConnect = () => {
  const {
    login,
    connectors: storeConnectors,
    connectWithConnector,
  } = useWeb3ClientsStore((s) => ({
    login: s.login,
    connectors: s.connectors as UIConnector[] | undefined, // wagmi/privy can populate this
    connectWithConnector: s.connectWithConnector as
      | ((id: string) => Promise<void> | void)
      | undefined,
  }));

  const [isPending, setPending] = useState(false);

  const connectors: UIConnector[] = Array.isArray(storeConnectors) ? storeConnectors : [];

  const connect = async (connectorId?: string) => {
    setPending(true);
    try {
      if (connectorId && connectWithConnector) {
        await connectWithConnector(connectorId);
        return;
      }
      if (connectorId) {
        const match = connectors.find((c) => c.id === connectorId);
        if (match?.connect) {
          await match.connect();
          return;
        }
      }
      if (login) {
        await login();
        return;
      }
      const firstWithConnect = connectors.find((c) => typeof c.connect === 'function');
      if (firstWithConnect?.connect) {
        await firstWithConnect.connect();
        return;
      }
      // noop: nothing wired in store
    } finally {
      setPending(false);
    }
  };

  return { connect, connectors, isPending };
};

const MOCK_ESTIMATE_GAS = async (_config: any, _args: any): Promise<bigint> => {
  return 1_000_000n;
};

const estimateGas = async (
  config: { publicClient?: PublicClient },
  params: Parameters<PublicClient['estimateGas']>[0],
) => {
  if (isMocked) {
    return MOCK_ESTIMATE_GAS(config, params);
  }
  const pc = config.publicClient;
  if (!pc) throw new Error('No publicClient');
  return pc.estimateGas(params);
};

const useAccountEffect = (opts: AccountEffectOpts) => {
  const { address, chainId, isConnected } = useAccount();
  const prevConnectedRef = useRef(false);
  const prevAddressRef = useRef<Address | null>(null);

  if (isMocked) {
    const firedRef = useRef(false);
    useEffect(() => {
      if (firedRef.current) return;
      firedRef.current = true;
      opts?.onConnect?.({
        address: DEFAULT_MOCK_ADDRESS as Address,
        chainId: SUPPORTED_CHAIN_IDS.ARBITRUM,
      });
      // Call opts?.onDisconnect?.() manually in tests if needed
    }, [opts]);
    return;
  }

  useEffect(() => {
    const prevConnected = prevConnectedRef.current;
    const prevAddress = prevAddressRef.current;

    if (isConnected && address && (!prevConnected || address !== prevAddress)) {
      opts?.onConnect?.({ address, chainId: chainId ?? null });
    }
    if (!isConnected && prevConnected) {
      opts?.onDisconnect?.();
    }
    prevConnectedRef.current = Boolean(isConnected);
    prevAddressRef.current = (address ?? null) as Address | null;
  }, [isConnected, address, chainId, opts]);
};

const useWaitForTransactionReceipt = ({ hash, confirmations, query }: UseWaitForTxArgs) => {
  const publicClient = usePublicClient();

  const [data, setData] = useState<any>(null);
  const [isSuccess, setSuccess] = useState(false);
  const [isError, setErr] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setLoading] = useState(false);

  const retryRef = useRef<NodeJS.Timeout | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const enabled = (query?.enabled ?? true) && Boolean(publicClient && hash);
  const refetchInterval = query?.refetchInterval ?? false;
  const retry = query?.retry ?? false;
  const retryDelay = query?.retryDelay ?? 1500;

  const clearTimers = () => {
    if (retryRef.current) clearTimeout(retryRef.current);
    if (pollRef.current) clearTimeout(pollRef.current);
    retryRef.current = null;
    pollRef.current = null;
  };

  const doFetch = useCallback(async () => {
    if (!enabled || !publicClient || !hash) return;
    try {
      setLoading(true);
      const receipt = await publicClient.waitForTransactionReceipt({
        hash,
        confirmations,
      });
      setData(receipt);
      setSuccess(true);
      setErr(false);
      setError(null);
    } catch (e) {
      setErr(true);
      setError(e as Error);
      if (retry && !isSuccess) {
        if (retryRef.current) clearTimeout(retryRef.current);
        retryRef.current = setTimeout(() => void doFetch(), retryDelay);
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, publicClient, hash, confirmations, retry, retryDelay, isSuccess]);

  const refetch = useCallback(async () => {
    clearTimers();
    await doFetch();
  }, [doFetch]);

  useEffect(() => {
    clearTimers();
    if (!enabled) {
      setLoading(false);
      return;
    }

    void doFetch();

    if (refetchInterval && typeof refetchInterval === 'number') {
      const tick = async () => {
        await doFetch();
        if (isSuccess || isError) return;
        pollRef.current = setTimeout(tick, refetchInterval);
      };
      pollRef.current = setTimeout(tick, refetchInterval);
    }

    return () => clearTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, hash, confirmations, refetchInterval, doFetch]);

  useEffect(() => {
    if (isSuccess || isError) clearTimers();
  }, [isSuccess, isError]);

  return {
    data,
    isSuccess,
    isError,
    error,
    refetch,
    fetchStatus: isLoading ? 'fetching' : 'idle',
    isLoading,
  };
};

const useReadContract = ({
  address,
  abi,
  functionName,
  args,
  query,
}: {
  address: Address | undefined;
  abi: any;
  functionName: string;
  args?: any[];
  query?: { enabled?: boolean; staleTime?: number; refetchOnWindowFocus?: boolean };
}) => {
  if (isMocked) {
    const mock =
      (MOCK_READ_CONTRACT as any)[functionName] ?? (MOCK_READ_CONTRACT as any).defaultError;

    if (query?.enabled === false) {
      return {
        data: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
        error: null as Error | null,
        refetch: async () => {},
      };
    }

    return {
      data: mock.data ?? null,
      isLoading: false,
      isSuccess: Boolean(mock.isSuccess),
      isError: Boolean(mock.isError),
      error: null as Error | null,
      refetch: async () => {
        return;
      },
    };
  }

  const publicClient = usePublicClient();
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const readContract = useCallback(async () => {
    if (!publicClient || !address || !abi || !functionName) return;
    if (query?.enabled === false) return;

    try {
      setIsLoading(true);
      setIsError(false);
      setError(null);
      const result = await publicClient.readContract({
        address,
        abi,
        functionName,
        args: args ?? [],
        authorizationList: undefined,
      });
      setData(result);
      setIsSuccess(true);
      setIsError(false);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Failed to read contract');
      setError(err);
      setIsError(true);
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  }, [publicClient, address, abi, functionName, args, query?.enabled]);

  const refetch = useCallback(async () => readContract(), [readContract]);

  useEffect(() => void readContract(), [readContract]);

  useEffect(() => {
    if (query?.refetchOnWindowFocus === false) return;
    const onFocus = () => {
      void readContract();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [readContract, query?.refetchOnWindowFocus]);

  return { data, isLoading, isSuccess, isError, error, refetch };
};

export {
  estimateGas,
  useAccount,
  useAccountEffect,
  useBlockNumber,
  useChainId,
  useConnect,
  useDisconnect,
  useFundWalletHandler,
  usePublicClient,
  useReadContract,
  useSignMessage,
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWalletClient,
};
